import { useState, type ReactNode } from 'react';
import { uploadProcessingLine, type ProcessingEstimate } from '../lib/processing-estimate';
import { summarizeUploads, uploadSummarySentence, type UploadOutcome } from '../lib/upload-summary';

/** Which name list is open. */
type Open = 'skipped' | 'failed' | null;

/**
 * The END-OF-UPLOAD SUMMARY (owner ask 2026-10-22), shared by the admin's upload
 * panel and the seller's submit page: once every picked file has an answer, one
 * plain sentence — "2,413 files: 760 added, 1,653 skipped as exact duplicates of
 * files already in the room, 2 failed to upload." — with the skipped and failed
 * names one click away (the failed ones with why). Nothing is shown while any
 * file is still queued or uploading, and nothing for an empty batch.
 */
export function UploadSummary({
  outcomes,
  processingEstimate,
  summaryAside,
  estimateAside,
}: {
  outcomes: readonly UploadOutcome[];
  /**
   * The room's processing estimate, read once the batch is done (owner ask
   * 2026-10-23): adds "Processing these will take about N minutes." when files
   * were added and work remains.
   */
  processingEstimate?: ProcessingEstimate | undefined;
  /** A "?" help tip shown after the summary sentence. */
  summaryAside?: ReactNode;
  /** A "?" help tip shown after the processing-time line (when it is shown). */
  estimateAside?: ReactNode;
}) {
  const [open, setOpen] = useState<Open>(null);
  const summary = summarizeUploads(outcomes);
  if (!summary.done) return null;
  const processingLine = summary.added > 0 ? uploadProcessingLine(processingEstimate) : null;
  const toggle = (which: Exclude<Open, null>) => setOpen(open === which ? null : which);
  return (
    <section
      aria-label="Upload summary"
      data-testid="upload-summary"
      className="space-y-2 rounded-md border bg-muted/40 px-3 py-2.5"
    >
      <div className="flex items-center gap-1.5">
        <p role="status" className="text-sm font-medium" data-testid="upload-summary-sentence">
          {uploadSummarySentence(summary)}
        </p>
        {summaryAside}
      </div>
      {processingLine ? (
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-ink-2" data-testid="upload-summary-eta">
            {processingLine}
          </p>
          {estimateAside}
        </div>
      ) : null}
      {summary.skipped > 0 || summary.failed > 0 ? (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {summary.skipped > 0 ? (
            <button
              type="button"
              aria-expanded={open === 'skipped'}
              onClick={() => toggle('skipped')}
              className="font-medium text-primary hover:underline"
            >
              {open === 'skipped' ? 'Hide' : 'Show'} the {summary.skipped.toLocaleString('en-US')}{' '}
              skipped
            </button>
          ) : null}
          {summary.failed > 0 ? (
            <button
              type="button"
              aria-expanded={open === 'failed'}
              onClick={() => toggle('failed')}
              className="font-medium text-destructive hover:underline"
            >
              {open === 'failed' ? 'Hide' : 'Show'} the {summary.failed.toLocaleString('en-US')}{' '}
              that failed
            </button>
          ) : null}
        </p>
      ) : null}
      {open === 'skipped' ? (
        <ul
          className="max-h-64 space-y-0.5 overflow-y-auto pr-1 text-sm"
          aria-label="Skipped files"
        >
          {summary.skippedNames.map((name, i) => (
            <li key={i} className="break-words">
              {name}
            </li>
          ))}
        </ul>
      ) : null}
      {open === 'failed' ? (
        <ul
          className="max-h-64 space-y-0.5 overflow-y-auto pr-1 text-sm"
          aria-label="Files that failed to upload"
        >
          {summary.failures.map((f, i) => (
            <li key={i} className="break-words">
              <span>{f.name}</span>
              {f.detail ? <span className="text-destructive"> — {f.detail}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
